import React from "react";

/* =============================
   FINAL STABLE VERSION (FULL PATCH)
   ============================= */

/* ---------- helpers ---------- */
const polar = (cx, cy, r, deg) => {
  const rad = (deg * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
};

const arc = (cx, cy, r, s, e) => {
  const a = polar(cx, cy, r, e);
  const b = polar(cx, cy, r, s);
  const large = e - s <= 180 ? 0 : 1;
  return `M ${a.x} ${a.y} A ${r} ${r} 0 ${large} 0 ${b.x} ${b.y}`;
};

/* ---------- formatter (NO DECIMALS) ---------- */
const fmt = (v) =>
  Math.round(Number(v || 0)).toLocaleString("en-IN");

/* ---------- pulse animation ---------- */
const pulseStyle = `
@keyframes dangerPulse {
  0% { filter: drop-shadow(0 0 0 rgba(239,68,68,0)); }
  50% { filter: drop-shadow(0 0 10px rgba(239,68,68,.45)); }
  100% { filter: drop-shadow(0 0 0 rgba(239,68,68,0)); }
}
.danger-pulse {
  animation: dangerPulse 1.4s ease-in-out infinite;
}
`;

/* ---------- Status Card ---------- */
const RiskStatusCard = ({ state }) => (
  <div
    className="rounded-lg px-4 py-2 text-center"
    style={{
      background:
  state === "LOCKED"
    ? "rgba(239,68,68,0.18)"
    : "rgba(16,185,129,0.16)",

      color: state === "LOCKED" ? "#ef4444" : "#10b981",
    }}
  >
    <div className="text-sm font-semibold tracking-[0.25em]">{state}</div>
  </div>
);

/* ---------- LED BAR RING ---------- */
const LedRing = ({ size, radius, pct, color, thick = false }) => {
  const start = -135;
  const total = 270;
  const bars = 24;
  const barAngle = total / bars;
  const activeBars = Math.round(pct * bars);

  return (
  <g className="text-gray-200 dark:text-zinc-700">


      <path
        d={arc(size / 2, size / 2, radius, start, start + total)}
        stroke="currentColor"
        strokeWidth={thick ? 22 : 18}
        fill="none"
      />
      {Array.from({ length: bars }).map((_, i) => {
        const s = start + i * barAngle + 1;
        const e = start + (i + 1) * barAngle - 1;
        return (
          <path
            key={i}
            d={arc(size / 2, size / 2, radius, s, e)}
            stroke={i < activeBars ? color : "currentColor"}

            strokeWidth={thick ? 16 : 12}
            fill="none"
          />
        );
      })}
    </g>
  );
};

/* ---------- Tech Meter ---------- */
const TechMeter = ({ label, value, pct, color }) => {
  const size = 120;
  const r = size / 2 - 16;

  return (
    <div className="flex flex-col items-center shrink-0">
      <svg width={size} height={size}>
        <LedRing size={size} radius={r} pct={pct} color={color} />
      </svg>
      <div className="text-[10px] tracking-widest text-muted-foreground mt-1">
        {label}
      </div>
      <div className="text-xs font-semibold text-foreground">{value}</div>
    </div>
  );
};

/* ---------- Bar Meter (mobile) ---------- */
const BarMeter = ({ label, value, pct, color }) => (
  <div className="w-28">
    <div className="text-[9px] tracking-widest text-muted-foreground mb-1 text-center">
      {label}
    </div>
    <div className="h-2 w-full bg-gray-200 dark:bg-white/10 rounded-full overflow-hidden">

      <div
        className="h-full"
        style={{ width: `${pct * 100}%`, backgroundColor: color }}
      />
    </div>
    <div className="text-[10px] font-semibold text-foreground mt-1 text-center">
      {value}
    </div>
  </div>
);

/* ---------- Risk Dial ---------- */
const RiskDial = ({
  totalPnl,
  realised,
  unrealised,
  lossLimit,
  profitLimit,
  activeLossFloor,
}) => {
  const size = 280;
  const r = size / 2 - 30;

  const isLoss = totalPnl < 0;
  const limit = isLoss ? lossLimit : profitLimit;
  const pct = limit > 0 ? Math.min(Math.abs(totalPnl) / limit, 1) : 0;

  /* color logic */
  let color = isLoss ? "#ef4444" : "#10b981";
  if (pct > 0.6 && !isLoss) color = "#f59e0b";
  if (pct >= 1) color = "#ef4444";

  /* pulse logic */
  const approachingFloor =
    isLoss &&
    typeof activeLossFloor === "number" &&
    totalPnl > activeLossFloor &&
    Math.abs(totalPnl) / Math.abs(activeLossFloor) >= 0.8;

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <style>{pulseStyle}</style>

      <svg
        width={size}
        height={size}
        className={approachingFloor ? "danger-pulse" : ""}
      >
        <LedRing size={size} radius={r} pct={pct} color={color} thick />
      </svg>

      <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
        <div className="text-4xl font-bold drop-shadow-sm" style={{ color }}>


          {totalPnl < 0 ? "-" : "+"}₹{fmt(Math.abs(totalPnl))}
        </div>
        <div className="text-[10px] tracking-widest text-muted-foreground mt-1">
          TOTAL P&amp;L
        </div>

        <div className="mt-2 flex gap-6">
          <div className="flex flex-col items-center">
            <div className="text-xs font-semibold text-emerald-600">
              +₹{fmt(Math.max(0, realised))}
            </div>
            <div className="text-[9px] tracking-widest text-muted-foreground">
              REALISED
            </div>
          </div>
          <div className="flex flex-col items-center">
            <div
              className={`text-xs font-semibold ${
                unrealised >= 0 ? "text-emerald-600" : "text-red-600"
              }`}
            >
              {unrealised < 0 ? "-" : "+"}₹{fmt(Math.abs(unrealised))}
            </div>
            <div className="text-[9px] tracking-widest text-muted-foreground">
              UNREALISED
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ---------- Info Box ---------- */
const InfoBox = ({ label, value, danger, accent }) => (
  <div className="
    glass- card rounded-lg p-2 text-center
    bg-card border border-border
  ">


    <div className="text-[10px] tracking-widest text-muted-foreground">{label}</div>
    <div
      className={`text-sm font-semibold ${
        danger
          ? "text-red-600"
          : accent
          ? "text-emerald-600"
          : "text-foreground"
      }`}
    >
      {value}
    </div>
  </div>
);

/* ---------- Shared Layout ---------- */
const GaugeLayout = ({
  pnl,
  realised,
  unrealised,
  maxLoss,
  maxProfit,
  peakProfit,
  activeLossFloor,
}) => (
 <div className="
  bg-transparent
  
  flex flex-col items-center pt-4 pb-6
">


    <div className="w-full max-w-4xl flex flex-col items-center gap-4">
      <RiskStatusCard state={pnl <= -maxLoss ? "LOCKED" : "SAFE"} />

      {/* Desktop */}
      <div className="hidden sm:flex items-center gap-6">
        <TechMeter
          label="Remaining"
          value={`₹${fmt(Math.max(0, maxLoss - Math.abs(pnl)))}`}
          pct={pnl < 0 ? Math.abs(pnl) / maxLoss : 0}
          color="#ef4444"
        />

        <RiskDial
          totalPnl={pnl}
          realised={realised}
          unrealised={unrealised}
          lossLimit={maxLoss}
          profitLimit={maxProfit}
          activeLossFloor={activeLossFloor}
        />

        <TechMeter
          label="Trailing"
          value={`₹${fmt(Math.max(0, pnl))}`}
          pct={pnl > 0 ? pnl / maxProfit : 0}
          color="#10b981"
        />
      </div>

      {/* Mobile */}
      <div className="sm:hidden flex flex-col items-center gap-3">
        <RiskDial
          totalPnl={pnl}
          realised={realised}
          unrealised={unrealised}
          lossLimit={maxLoss}
          profitLimit={maxProfit}
          activeLossFloor={activeLossFloor}
        />

        <div className="flex gap-4">
          <BarMeter
            label="Remaining"
            value={`₹${fmt(Math.max(0, maxLoss - Math.abs(pnl)))}`}
            pct={pnl < 0 ? Math.abs(pnl) / maxLoss : 0}
            color="#ef4444"
          />
          <BarMeter
            label="Trailing"
            value={`₹${fmt(Math.max(0, pnl))}`}
            pct={pnl > 0 ? pnl / maxProfit : 0}
            color="#10b981"
          />
        </div>
      </div>

      {/* Info Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mt-2 w-full max-w-4xl">
        <InfoBox label="MAX LOSS" value={`₹${fmt(maxLoss)}`} />
        <InfoBox label="MAX PROFIT" value={`₹${fmt(maxProfit)}`} />
        <InfoBox label="PEAK PROFIT" value={`₹${fmt(peakProfit)}`} accent />
        <InfoBox
          label="ACTIVE LOSS FLOOR"
          value={`₹${fmt(activeLossFloor)}`}
          danger
        />
        <div className="hidden sm:block">
          <InfoBox label="LAST TRADE CLOSE" value="15:12" />
        </div>
      </div>
    </div>
  </div>
);

/* ---------- Preview ---------- */
const PreviewLayout = () => (
  <GaugeLayout
    pnl={-2300}
    realised={-1400}
    unrealised={-900}
    maxLoss={5000}
    maxProfit={10000}
    peakProfit={3200}
    activeLossFloor={-1500}
  />
);

/* ---------- Live ---------- */
const LiveLayout = ({
  total = 0,
  realised = 0,
  unrealised = 0,
  maxLoss = 1,
  maxProfit = 1,
  peakProfit = 0,
  activeLossFloor = 0,
}) => (
  <GaugeLayout
    pnl={total}
    realised={realised}
    unrealised={unrealised}
    maxLoss={maxLoss}
    maxProfit={maxProfit}
    peakProfit={peakProfit}
    activeLossFloor={activeLossFloor}
  />
);

/* ---------- Export ---------- */
export default function CircularGauge270(props) {
  const hasAdminKey =
    typeof window !== "undefined" &&
    !!localStorage.getItem("admin_key");

  return hasAdminKey ? <LiveLayout {...props} /> : <PreviewLayout />;
}
